import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import esCommon from './locales/es/common.json';
import enCommon from './locales/en/common.json';

export const defaultNS = 'common';

/**
 * The languages the app ships (Spanish primary). Adding one is dropping
 * `locales/<code>/common.json` in, registering it in {@link resources}, adding the
 * code here and a label in `LANGUAGE_LABELS` — no other file needs to change.
 */
export const SUPPORTED_LANGUAGES = ['es', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Each language named in itself, for the switcher. */
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  es: 'Español',
  en: 'English',
};

/** The localStorage key i18next caches the user's choice under. */
export const LANGUAGE_STORAGE_KEY = 'kura-payments.language';

export const resources = {
  es: { common: esCommon },
  en: { common: enCommon },
} as const;

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Spanish by default: the detector only honours an explicit choice cached in
    // localStorage (made in the switcher) and otherwise falls back to Spanish.
    // The browser language is deliberately NOT consulted — the product is
    // Spanish-first and English is opt-in.
    fallbackLng: 'es',
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // A cached `es-ES` / `en-GB` resolves to the bare code we ship.
    load: 'languageOnly',
    ns: ['common'],
    defaultNS,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
    },
  });

// Keep the document language and title in sync with the active language.
// The product name lives in the browser tab, never retyped under the wordmark
// (design-system brand rule), so `app.name` stays a reachable, translated key.
const syncDocument = (lng: string) => {
  document.documentElement.lang = lng;
  document.title = i18n.t('app.name');
};
syncDocument(i18n.resolvedLanguage ?? 'es');
i18n.on('languageChanged', syncDocument);

export default i18n;
